import { useState, useEffect } from "react";
import { Github, Linkedin, Instagram, Send, FileDownIcon } from "lucide-react"; // Using Lucide for classy icons
import { trackEvent } from "./utils/analytics";

const roles = [
  "Full Stack Developer",
  "Software Developer",
  "Frontend Developer",
  "React Developer",
  "UI Developer",
  "Software Engineer",
  "UI Engineer",
  "Frontend Engineer",
];

const Hero = () => {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);

  useEffect(() => {
    if (document.hidden) return;
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (subIndex === roles[index].length + 1 && !reverse) {
      setTimeout(() => setReverse(true), 2000);
      return;
    }

    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((prev) => (prev + 1) % roles.length);
      return;
    }

    const timeout = setTimeout(
      () => {
        setSubIndex((prev) => prev + (reverse ? -1 : 1));
      },
      reverse ? 75 : 150,
    );

    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse]);

  const resumeUrl = new URL("./static/resume.pdf", import.meta.url).href;
  let cachedResumeBlobUrl = null;
  const downloadResume = async () => {
    if (!cachedResumeBlobUrl) {
      const response = await fetch(resumeUrl);
      const blob = await response.blob();
      cachedResumeBlobUrl = URL.createObjectURL(blob);
    }

    const link = document.createElement("a");
    link.href = cachedResumeBlobUrl;
    link.download = "Shubham_Ghule_Resume.pdf";
    link.click();
    trackEvent("click_download_resume");
  };

  return (
    <section
      aria-labelledby="hero-heading"
      className="min-h-screen flex flex-col items-center justify-center px-6 pt-10 md:pt-20 hero-mesh-gradient transition-colors duration-700 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern grid-mask pointer-events-none" />
      <div className="hidden md:block absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 blur-[120px] rounded-full -z-10" />
      <div className="hidden md:block absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 blur-[120px] rounded-full -z-10" />
      <div className="text-center z-10">
        <h4 className="text-cyan-600 dark:text-cyan-400 font-bold tracking-[0.3em] md:text-left text-center uppercase text-xl  mb-6">
          Hi there...
        </h4>
        <h1
          id="hero-heading"
          className="text-4xl md:text-8xl font-bold mb-4 md:mb-8 tracking-tighter text-slate-900 dark:text-white transition-all duration-500">
          I'm{" "}
          <span className="bg-clip-text text-transparent bg-linear-to-r from-cyan-600 via-blue-600 to-indigo-600 dark:from-cyan-300 dark:via-cyan-600 dark:to-cyan-900">
            Shubham Ghule
          </span>
        </h1>
        <div className="h-12 flex items-center justify-center mb-4">
          <p className="text-xl md:text-3xl text-slate-500 dark:text-gray-400 font-light tracking-wide">
            {roles[index].substring(0, subIndex)}
            <span
              aria-hidden="true"
              className="inline-block w-0.75 h-6 md:h-8 bg-cyan-500 ml-2 animate-pulse"
            />
          </p>
        </div>
        <p className="mt-2 md:mt-4 text-[10px] md:text-xs uppercase tracking-[0.4em] text-gray-500 font-bold">
          "Transforming complex logic into intuitive interfaces"
        </p>
        <div className="flex gap-10 justify-center mt-6 md:mt-12">
          {[
            {
              Icon: Linkedin,
              link: "https://linkedin.com/in/shubh27842",
              label: "Linkedin",
            },
            {
              Icon: Github,
              link: "https://github.com/shubh27842",
              label: "Github",
            },
            { Icon: Instagram, link: "#", label: "Instagram" },
          ].map((item, i) => (
            <a
              key={i}
              href={item.link}
              onClick={() => trackEvent('click_profile_view', { platform: item.label })}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 dark:text-gray-600 hover:text-cyan-500 dark:hover:text-cyan-400 hover:scale-125 transition-all duration-300"
              aria-label={`Link to ${item.label}`}>
              <item.Icon size={32} strokeWidth={1.5} />
            </a>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-6 justify-center align-middle mt-12  px-[15%]">
          <button
            onClick={downloadResume}
            className=" flex items-center gap-2 px-8 py-4 font-black uppercase tracking-[0.2em] text-slate-900 text-[10px] rounded-full bg-cyan-400 cursor-pointer">
            <FileDownIcon size={18} />
            Download Resume
          </button>
          <a
            href="#contact"
            className="btn-secondary-glass group flex items-center gap-2 px-8 py-4 rounded-full transition-all duration-500 hover:scale-105 active:scale-95 cursor-pointer">
            <Send
              size={18}
              className="text-cyan-600 dark:text-cyan-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
            <span className="text-slate-900 dark:text-cyan-400 font-black uppercase tracking-[0.2em] text-[10px]">
              Contact Me
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Hero;
